import axios from 'axios'
import { interceptorsRequest, getRenewToken, setToken, setRenewToken, removeToken, getTenant } from '@/utils/auth'
import { ElMessage } from 'element-plus'
import xRouter from '@/router'

// 刷新token
const renewToken = async (): Promise<boolean> => {
  try {
    // 修复重复的gw-api前缀
    const res = await fetch('/gw-api/system/auth/renewToken', {
      method: 'POST',
      headers: {
        Tenant: getTenant(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ renewToken: getRenewToken() }),
    })

    const data = await res.json()
    if (data.code == 200 && data.data.length > 0) {
      setToken(data.data[0])
      setRenewToken(data.data[1])
      return true // token刷新成功
    }
    if (data.code == 401 || data.code == 405 || data.data?.length == 0) {
      removeToken()
      ElMessage.error('会话已过期，请重新登录')
      xRouter.push({
        path: '/login',
      })
      return false // token刷新失败
    }
    return false
  } catch (error) {
    console.error('Failed to renew token:', error)
    removeToken()
    ElMessage.error('会话已过期，请重新登录')
    xRouter.push({
      path: '/login',
    })
    return false // token刷新失败
  }
}

// 定义环境变量
const baseURL = import.meta.env.VITE_API_BASE_URL
const service = axios.create({
  baseURL: baseURL, // 动态注入当前环境的域名
  timeout: 60000, // 请求超时时间
})

// 请求拦截器
service.interceptors.request.use(interceptorsRequest, (error) => {
  console.error(error)
  return Promise.reject(new Error(error))
})

// 响应拦截器
service.interceptors.response.use(
  (response) => {
    const res = response.data
    if (res.code === 200 || (response.config.url.startsWith('/container') && res.code == 0)) {
      return res // 返回data部分，与组件期望的格式一致
    } else {
      if (res.code === 401) {
        // 检查是否已经在重试中，避免无限重试
        const config: any = response.config
        if (!config._retry) {
          config._retry = true
          return renewToken().then((tokenRenewed) => {
            if (tokenRenewed) {
              // token刷新成功，重新执行原请求
              // 创建一个新的配置对象，避免修改原始配置
              const newConfig: any = { ...config }
              // 清除可能已经添加的前缀
              if (newConfig.url && (newConfig.url.startsWith('/gw-api/') || newConfig.url.startsWith('/container_proxy/api/'))) {
                // 直接使用原始axios实例，并且手动设置正确的URL格式
                return axios({
                  ...newConfig,
                  baseURL: baseURL,
                  headers: {
                    ...newConfig.headers,
                    Authorization: `Bearer ${localStorage.getItem('saas-token')}`,
                  },
                }).then((retryResponse) => {
                  // 关键修复：确保重试请求返回与原始请求相同格式的数据
                  const retryRes = retryResponse.data
                  if (retryRes.code === 200 || (newConfig.url.startsWith('/container') && retryRes.code == 0)) {
                    return retryRes // 返回data部分，与组件期望的格式一致
                  } else {
                    return Promise.reject(new Error(retryRes?.msg || 'Error'))
                  }
                })
              }

              // 对于没有前缀的URL，更新token后使用service重新请求
              return service({
                ...newConfig,
                headers: {
                  ...newConfig.headers,
                  Authorization: `Bearer ${localStorage.getItem('saas-token')}`,
                },
              })
            }
            // token刷新失败，拒绝Promise
            return Promise.reject(new Error('Token刷新失败，请重新登录'))
          })
        }
      } else if (res.code === 403) {
        ElMessage.error('没有权限访问该资源')
      } else {
        ElMessage({
          message: res.msg || 'Error',
          type: 'error',
          duration: 3 * 1000,
        })
      }
      return Promise.reject(new Error(res?.msg || 'Error'))
    }
  },
  (error) => {
    console.error(error)
    ElMessage({
      message: error.msg || '系统异常',
      type: 'error',
      duration: 3 * 1000,
    })
    return Promise.reject(new Error(error.msg || '系统异常'))
  },
)

export default service
